import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EMPTY_ARRAY,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  SlippageControl,
  Toggle,
  getTreeRowBlotterGroupColDef,
  useAccordionFilterBuilder,
  useBlotterTable,
  useCallbackRef,
  usePersistedBlotterTable,
  usePopoverState,
  type Quote,
  type Slippage,
} from '@talos/kyoko';

import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { OMSView } from '@src/components/OMS/OMSView';
import { RfqBestQuoteByToggle } from '@src/components/OMS/RfqBestQuoteByToggle';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { GridOptions } from 'ag-grid-community';
import { useMemo, useRef } from 'react';
import { showRFQ } from '../../../components/OMS/NewRFQ/RFQSlice';
import { openView } from '../../../components/OMS/OMSSlice';
import { BLOTTER_TRADE_BEST_SLIPPAGE } from '../../../tokens/tooltips';
import { QuoteRow, type QuotesBlotterRow } from './types';
import { useQuotesV2BlotterDataObs } from './useQuotesV2BlotterDataObs';
import { useQuotesV2Columns } from './useQuotesV2Columns';
import type { QuotesV2TableFilter } from './useQuotesV2Filter';
import { useQuotesV2Filter } from './useQuotesV2Filter';
import { useQuotesV2Menu } from './useQuotesV2Menu';

export interface QuotesV2BlotterSettings {
  showAllDecimals?: boolean;
  groupByInitiatingFrom?: boolean;
  groupByParentRFQID?: boolean;
}

export interface QuotesV2BlotterTableProps {
  blotterID: string;
  defaultFilter?: QuotesV2TableFilter;
  showStatusFilter?: boolean;
  initialIsOpen?: boolean;
  settings?: QuotesV2BlotterSettings;
  onSettingsChanged?: (settings: QuotesV2BlotterSettings) => void;
}

export function QuotesV2BlotterTable({
  blotterID,
  defaultFilter,
  initialIsOpen,
  settings,
  showStatusFilter,
  onSettingsChanged,
}: QuotesV2BlotterTableProps) {
  const { enableTakerMarketplaceETFRFQFlow, enrichRFQBlotterV2WithCareOrders } = useFeatureFlag();
  const {
    showAllDecimals,
    groupByInitiatingFrom: inputGroupByInitiatingFrom,
    groupByParentRFQID: inputGroupByParentRFQID,
  } = settings ?? {};
  const { showAllDecimalsInBlotters, allowedSlippage, setAllowedSlippage, rfqBestQuoteBy } = useTradingSettings();
  const dispatch = useAppStateDispatch();

  const groupByInitiatingFrom = inputGroupByInitiatingFrom && enrichRFQBlotterV2WithCareOrders; // only apply the setting if we're working with care orders
  const groupByParentRFQID = inputGroupByParentRFQID && enrichRFQBlotterV2WithCareOrders && !groupByInitiatingFrom;
  const showGroupByToggles =
    enrichRFQBlotterV2WithCareOrders && enableTakerMarketplaceETFRFQFlow && onSettingsChanged != null;

  const onOpenQuote = useCallbackRef((quote: Quote) => {
    dispatch(openView(OMSView.RFQForm));
    dispatch(showRFQ(quote));
  });

  const { openDetailsDrawer } = useDetailsDrawer();
  const onShowDetails = useCallbackRef((quote: Quote) => {
    openDetailsDrawer({ entity: quote });
  });

  const handleRowDoubleClicked: NonNullable<GridOptions<QuotesBlotterRow>['onRowDoubleClicked']> = useCallbackRef(
    ({ data }) => {
      if (data instanceof QuoteRow) {
        onShowDetails(data.quote);
      }
    }
  );

  const columns = useQuotesV2Columns({ onOpenQuote, onShowDetails });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const { filterFn, filter, blotterTableFilterProps, filterBuilderProps } = useQuotesV2Filter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    showStatusFilter,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { getContextMenuItems, dialogs } = useQuotesV2Menu({
    filterableProperties: filterBuilderProps.properties,
    openClause: filterBuilderAccordion.openClause,
  });

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: (showAllDecimalsInBlotters || showAllDecimals) ?? false,
    }),
    [showAllDecimalsInBlotters, showAllDecimals]
  );

  const gridOptions: GridOptions<QuotesBlotterRow> = useMemo(() => {
    const groupHeaderPrefix = [
      groupByParentRFQID ? 'Care Order ID' : null,
      groupByInitiatingFrom ? 'Initiating From' : null,
    ]
      .filter(Boolean)
      .join(' > ');

    return {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
      treeData: true,
      getDataPath: row => row.dataPath,
      processUnpinnedColumns: () => EMPTY_ARRAY,
      getContextMenuItems,
      onRowDoubleClicked: handleRowDoubleClicked,
      autoGroupColumnDef: {
        headerName: groupHeaderPrefix ? `${groupHeaderPrefix} > RFQ > Market` : 'RFQ > Market',
        width: 200,
        editable: false,
        suppressColumnsToolPanel: false,
        cellRendererParams: {
          suppressCount: true,
        },
        sortable: false,
        pinned: 'left',
        ...getTreeRowBlotterGroupColDef(),
      },
    };
  }, [getContextMenuItems, groupByInitiatingFrom, groupByParentRFQID, handleRowDoubleClicked]);

  const options = useMemo(
    () => ({
      groupByInitiatingFrom: groupByInitiatingFrom ?? false,
      groupByParentRFQID,
      bestBy: rfqBestQuoteBy,
    }),
    [groupByInitiatingFrom, groupByParentRFQID, rfqBestQuoteBy]
  );

  const { dataObservable, errorObservable } = useQuotesV2BlotterDataObs({
    filterFn,
    filter,
    options,
  });

  const blotterTable = useBlotterTable<QuotesBlotterRow>({
    dataObservable,
    errorObservable,
    columns,
    persistence: persistedBlotterTable,
    rowID: 'rowID' satisfies keyof QuotesBlotterRow,
    gridOptions,
    settings: blotterTableSettings,
    exportOptions: {
      name: 'RFQs',
      baseParams: { skipRowGroups: false },
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const slippagePopoverContentRef = useRef<HTMLDivElement | null>(null);
  const extrasMenuCloseRef = useRef<() => void>(() => {});

  const isInsideSlippagePopover = (target: EventTarget | null) => {
    const node = target as Node | null;
    if (node && slippagePopoverContentRef.current?.contains(node)) {
      return true;
    }
    return false;
  };

  const extrasMenuPopover = usePopoverState({
    closeOnClickOutside: false,
    trigger: 'click',
    placement: 'bottom-end',
    usePortal: true,
    onClickOutside: e => {
      if (isInsideSlippagePopover(e.target)) {
        return;
      }

      extrasMenuCloseRef.current();
    },
  });
  extrasMenuCloseRef.current = extrasMenuPopover.close;

  const handleExpandAllGroups = useCallbackRef(() => {
    expandAllGroups();
  });

  const handleCollapseAllGroups = useCallbackRef(() => {
    collapseAllGroups();
  });

  const handleApplySlippage = useCallbackRef((value: Slippage) => {
    setAllowedSlippage(value);
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={handleExpandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={handleCollapseAllGroups}
              data-testid="collapse-all-button"
            />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            {showGroupByToggles && (
              <Toggle
                label="Group by Initiating From"
                size={FormControlSizes.Small}
                checked={groupByInitiatingFrom ?? false}
                onChange={value =>
                  onSettingsChanged({
                    ...settings,
                    /* Only one of these is allowed to be true at once */
                    groupByInitiatingFrom: value,
                    groupByParentRFQID: value ? false : groupByParentRFQID,
                  })
                }
                data-testid="group-by-initiating-from-toggle"
              />
            )}
            {showGroupByToggles && (
              <Toggle
                label="Group by Care Order ID"
                size={FormControlSizes.Small}
                checked={groupByParentRFQID ?? false}
                onChange={value =>
                  onSettingsChanged({
                    ...settings,
                    /* Only one of these is allowed to be true at once */
                    groupByParentRFQID: value,
                    groupByInitiatingFrom: value ? false : groupByInitiatingFrom,
                  })
                }
                data-testid="group-by-care-order-id-toggle"
              />
            )}
            {!showAllDecimalsInBlotters && onSettingsChanged != null && (
              <Toggle
                label="Show All Decimals"
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={value => onSettingsChanged({ ...settings, showAllDecimals: value })}
              />
            )}

            <RfqBestQuoteByToggle size={FormControlSizes.Small} />

            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            <Divider />

            <SlippageControl
              slippage={allowedSlippage}
              onApply={handleApplySlippage}
              popoverContentRef={slippagePopoverContentRef}
              helpTooltip={BLOTTER_TRADE_BEST_SLIPPAGE(rfqBestQuoteBy)}
              placement="bottom"
            />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {dialogs}
    </>
  );
}
